import { useEffect, useState } from 'react';
import { FaSpinner, FaUser } from 'react-icons/fa';
import { FiDownload, FiEdit2 } from 'react-icons/fi';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toastSuccess, toastError } from '../utils/toasts';
import HorizontalTabs from '../components/atoms/HorizontalTabs';
import AdminSectionTitle from '../components/AdminSectionTitle';
import FilterTable, { BaseData } from '../components/Table/Table';
import MenuDropdown from '../components/atoms/MenuDropdown';
import EmptyTable from '../components/Table/EmptyTable';
import CustomModal from '../components/atoms/CustomModal';
import Input from '../components/atoms/Input';
import Button from '../components/atoms/Button';
import {
  useGetBusinessMembersQuery, useUpdateBusinessMemberStatusMutation, useUpdateBusinessMemberMutation, useCreateBusinessMemberMutation,
  useExportMembersXlsxMutation,
  useDestroyBusinessMemberMutation,
} from '../store';
import { parseFriendlyDateAndHour } from '../utils/dates';
import { BusinessMemberDTO, BusinessMemberUpdateDTO } from '../models/business';
import useGetSelectedBusiness from '../hooks/useGetSelectedBusiness';
import config from '../config/config';
import PhotoUpload from '../components/PhotoUpload';
import { IconPlay2 } from '../components/icons';

interface TableRow extends BaseData {
  name: string;
  display_name: JSX.Element;
  tax_number: string;
  display_tax_number: JSX.Element;
  birth_date: string;
  display_birth_date: JSX.Element;
  phone: string;
  display_phone: JSX.Element;
  email: string;
  display_email: JSX.Element;
  photo_url: string;
  referral_name: string;
  socials: string;
}

const MembersPage = () => {
  const { business } = useGetSelectedBusiness();
  const { data: members, refetch } = useGetBusinessMembersQuery(
    { businessId: business?.id ?? '' },
    { skip: !business?.id },
  );
  const [destroyBusinessMember, { isLoading: isDestroyingMember, isSuccess: isDestroyingMemberSuccess }] = useDestroyBusinessMemberMutation();
  const [updateBusinessMemberStatus, { isLoading: isUpdatingStatus, isSuccess: isUpdatingStatusSuccess }] = useUpdateBusinessMemberStatusMutation();
  const [updateBusinessMember, { isLoading: isUpdatingMember, isSuccess: isUpdatingMemberSuccess }] = useUpdateBusinessMemberMutation();
  const [createBusinessMember, { isLoading: isCreatingMember, isSuccess: isCreatingMemberSuccess }] = useCreateBusinessMemberMutation();
  const [downloadMembers, { isLoading: isDownloadingXlsx, isSuccess: isDownloadingXlsxSuccess }] = useExportMembersXlsxMutation();
  const [tab, setTab] = useState<'associates' | 'pending' | 'blacklist'>('associates');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'view' | 'edit' | 'sendToBlacklist' | 'remove' | 'resend' | 'reset' | 'accept' | 'addMember'>('view');
  const [selectedRow, setSelectedRow] = useState<TableRow | null>(null);
  const [data, setData] = useState<BusinessMemberDTO[]>([]);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const {
    register, handleSubmit, formState: { errors }, setValue,
  } = useForm<BusinessMemberUpdateDTO>({
    resolver: yupResolver(
      yup.object().shape({
        full_name: yup.string().required('El nombre y apellido es requerido'),
        person_id: yup.string().required('El DNI es requerido').matches(/^\d+$/, 'El DNI debe contener solo números'),
        birth_date: yup.string().required('La fecha de nacimiento es requerida'),
        phone_number: yup.string().required('El número de teléfono es requerido').matches(/^\d+$/, 'El número de teléfono debe contener solo números'),
        email: yup.string().required('El email es requerido').email('El email no es válido'),
        referral_name: yup.string().optional(),
        socials: yup.string().optional(),
      }),
    ),
  });

  const formatBirthDate = (dateString: string) => {
    if (!dateString) {
      return '';
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    const [year, month, day] = dateString.split('T')[0].split('-');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (members) {
      if (tab === 'associates') {
        setData(members.filter((member) => member.status === 'accepted'));
      } else if (tab === 'pending') {
        setData(members.filter((member) => member.status === 'pending'));
      } else if (tab === 'blacklist') {
        setData(members.filter((member) => member.status === 'blacklisted'));
      }
    }
  }, [members, tab]);

  useEffect(() => {
    if (selectedRow) {
      setValue('full_name', selectedRow.name);
      setValue('person_id', selectedRow.tax_number);
      setValue('birth_date', selectedRow.birth_date);
      setValue('phone_number', selectedRow.phone);
      setValue('email', selectedRow.email);
      setValue('referral_name', selectedRow.referral_name);
      setValue('socials', selectedRow.socials);
    }
  }, [selectedRow]);

  useEffect(() => {
    if (isCreatingMemberSuccess) {
      setShowModal(false);
      toastSuccess('Socio agregado con éxito');
    }
  }, [isCreatingMemberSuccess]);

  useEffect(() => {
    if (isDownloadingXlsxSuccess) {
      toastSuccess('Socios exportados con éxito');
    }
  }, [isDownloadingXlsxSuccess]);

  useEffect(() => {
    if (isUpdatingStatusSuccess) {
      setShowModal(false);
      toastSuccess('Solicitud aceptada con éxito');
    }
  }, [isUpdatingStatusSuccess]);

  useEffect(() => {
    if (isUpdatingMemberSuccess) {
      setShowModal(false);
      toastSuccess('Datos actualizados con éxito');
    }
  }, [isUpdatingMemberSuccess]);

  useEffect(() => {
    if (isDestroyingMemberSuccess) {
      setShowModal(false);
      toastSuccess('Socio rechazado/eliminado con éxito');
    }
  }, [isDestroyingMemberSuccess]);

  useEffect(() => {
    if (business?.id) {
      refetch();
    }
  }, [tab, business?.id]);

  const handleSubmitAddMember = (dataSubmit: BusinessMemberUpdateDTO) => {
    if (!business?.id) {
      return;
    }
    try {
      createBusinessMember({
        businessId: business.id,
        dto: {
          full_name: dataSubmit.full_name,
          person_id: dataSubmit.person_id,
          birth_date: dataSubmit.birth_date,
          phone_number: dataSubmit.phone_number,
          email: dataSubmit.email,
          status: tab === 'blacklist' ? 'blacklisted' : 'accepted',
          photo_url: null,
          referral_name: dataSubmit.referral_name,
          socials: dataSubmit.socials,
        },
      });
    } catch (error) {
      console.log(error);
    }
  };

  const handleSubmitEdit = (dataSubmit: BusinessMemberUpdateDTO) => {
    if (!business?.id || !selectedRow?.id) {
      return;
    }
    try {
      updateBusinessMember({
        businessId: business.id,
        memberId: selectedRow.id,
        dto: {
          full_name: dataSubmit.full_name,
          person_id: dataSubmit.person_id,
          birth_date: dataSubmit.birth_date,
          phone_number: dataSubmit.phone_number,
          email: dataSubmit.email,
          referral_name: dataSubmit.referral_name,
          socials: dataSubmit.socials,
          photo_url: photoUrl || selectedRow.photo_url,
        },
      });
    } catch (error) {
      console.log(error);
    }
  };

  const downloadImage = async (url?: string, filename?: string) => {
    try {
      if (!url) {
        return;
      }
      const targetUrl = new URL(url, window.location.href);
      const isSameOrigin = targetUrl.origin === window.location.origin;

      if (!isSameOrigin) {
        const a = document.createElement('a');
        a.href = targetUrl.toString();
        a.download = filename || 'imagen.jpg';
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        return;
      }

      const response = await fetch(targetUrl.toString(), { mode: 'cors' });
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename || 'imagen.jpg';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(objectUrl);
      document.body.removeChild(a);
    } catch (e) {
      console.error(e);
      toastError('No se pudo descargar la imagen');
    }
  };

  const triggerPhotoUpload = (inputId: string) => {
    const input = document.getElementById(inputId) as HTMLInputElement | null;
    input?.click();
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(`${business?.nickname}.${config.baseDomain}/socios`);
    toastSuccess('Link copiado al portapapeles');
  };

  const handleSubmitAccept = (row: TableRow) => {
    if (!business?.id) {
      return;
    }
    try {
      updateBusinessMemberStatus({ businessId: business.id, memberId: row.id, status: 'accepted' });
    } catch (error) {
      console.log(error);
    }
  };

  const handleSubmitReject = (row: TableRow) => {
    if (!business?.id) {
      return;
    }
    try {
      destroyBusinessMember({ businessId: business.id, memberId: row.id });
    } catch (error) {
      console.log(error);
    }
  };

  const handleSubmitBlacklist = (row: TableRow) => {
    if (!business?.id) {
      return;
    }
    try {
      updateBusinessMemberStatus({ businessId: business.id, memberId: row.id, status: 'blacklisted' });
    } catch (error) {
      console.log(error);
    }
  };

  const handleSubmitReset = (row: TableRow) => {
    if (!business?.id) {
      return;
    }
    try {
      updateBusinessMemberStatus({ businessId: business.id, memberId: row.id, status: 'pending' });
    } catch (error) {
      console.log(error);
    }
  };

  const handleSendToBlacklist = (row: TableRow) => {
    if (row) {
      setShowModal(true);
      setModalType('sendToBlacklist');
      setSelectedRow(row);
    }
  };

  const handleAddMember = () => {
    setValue('full_name', '');
    setValue('person_id', '');
    setValue('birth_date', '');
    setValue('phone_number', '');
    setValue('email', '');
    setShowModal(true);
    setModalType('addMember');
  };

  const handleView = (row: TableRow) => {
    setPhotoUrl(row.photo_url);
    setShowModal(true);
    setModalType('view');
    setSelectedRow(row);
  };

  const handleAccept = (row: TableRow) => {
    setPhotoUrl(row.photo_url);
    setShowModal(true);
    setModalType('accept');
    setSelectedRow(row);
  };

  const handleEdit = (row: TableRow) => {
    setPhotoUrl(row.photo_url);
    setShowModal(true);
    setModalType('edit');
    setSelectedRow(row);
  };

  const handleRemove = (row: TableRow) => {
    setShowModal(true);
    setModalType('remove');
    setSelectedRow(row);
  };

  const handleResend = (row: TableRow) => {
    setShowModal(true);
    setModalType('resend');
    setSelectedRow(row);
  };

  const handleReset = (row: TableRow) => {
    setShowModal(true);
    setModalType('reset');
    setSelectedRow(row);
  };

  const rows: TableRow[] = data?.map((member) => ({
    id: member.id,
    name: member.full_name,
    display_name: <p className='whitespace-nowrap'>{member.full_name}</p>,
    tax_number: member.person_id,
    display_tax_number: <p className='whitespace-nowrap'>{member.person_id}</p>,
    birth_date: formatBirthDate(member.birth_date),
    display_birth_date: <p className='whitespace-nowrap'>{parseFriendlyDateAndHour(member.birth_date, false, true, true)}</p>,
    phone: member.phone_number,
    display_phone: <p className='whitespace-nowrap'>{member.phone_number}</p>,
    email: member.email,
    display_email: <p className='whitespace-nowrap'>{member.email}</p>,
    photo_url: member.photo_url,
    referral_name: member.referral_name || 'No informado',
    socials: member.socials || 'No informado',
  })) || [];

  const headCells = [
    {
      id: 'display_name',
      label: 'Nombre y Apellido',
    },
    {
      id: 'display_tax_number',
      label: 'DNI',
    },
    {
      id: 'display_birth_date',
      label: 'Fecha de nacimiento',
    },
    {
      id: 'display_phone',
      label: 'Número de celular',
    },
  ];

  const actions = (row: TableRow) => (
    <MenuDropdown
      data={row}
      options={[
        ...(tab === 'associates' && [{ label: 'Ver datos del socio', onClick: () => handleView(row) }, { label: 'Editar datos', onClick: () => handleEdit(row) }, { label: 'Eliminar socio', onClick: () => handleRemove(row) }, { label: 'Reenviar credencial de socio', onClick: () => handleResend(row) }]) || [],
        ...(tab === 'pending' && [{ label: 'Ver datos de la solicitud', onClick: () => handleAccept(row) }, { label: 'Editar datos', onClick: () => handleEdit(row) }]) || [],
        ...(tab !== 'blacklist' && [{ label: 'Enviar a lista negra', onClick: () => handleSendToBlacklist(row) }]) || [{ label: 'Restablecer como socio', onClick: () => handleReset(row) }, { label: 'Eliminar cliente', onClick: () => handleRemove(row) }],
      ]}
    />
  );

  const customButton = [<Button onClick={handleAddMember} variant='secondary'>
    <div className='flex flex-row gap-2 items-center'>
      <img src='/user-plus-black.svg' alt='' className='min-w-[20px] min-h-[20px] w-5 h-5' />
      <p className='whitespace-nowrap hidden md:block'>Agregar socio</p>
    </div>
  </Button>,
  ...((tab === 'associates') ? [<Button onClick={() => {
    if (business?.id) {
      downloadMembers({ businessId: business.id });
    }
  }} variant='secondary' loading={isDownloadingXlsx}>
    <div className='flex flex-row gap-2 items-center'>
      <img src='/arrow-down-circle.svg' alt='' className='min-w-[20px] min-h-[20px] w-5 h-5' />
      <p className='whitespace-nowrap hidden md:block'>Exportar socios</p>
    </div>
  </Button>] : []),
  ];

  return (
    <div className="flex flex-col gap-8 my-6">
      <AdminSectionTitle
        title={business?.name || 'Productoras'}
        subtitle={'Socios'}
        icon={<IconPlay2 className='w-12 h-12 rotate-180' />}
        mainButtons={[
          {
            label: 'Link inscripción', onClick: handleCopyLink, variant: 'secondary', image: '/link.svg',
          },
        ]}
      />
      <div className="glass-surface flex flex-col gap-8">
        <HorizontalTabs
          tabs={[
            { label: 'Listado de socios', onClick: () => setTab('associates'), selected: tab === 'associates' },
            { label: 'Solicitudes pendientes', onClick: () => setTab('pending'), selected: tab === 'pending' },
            { label: 'Lista negra', onClick: () => setTab('blacklist'), selected: tab === 'blacklist' },
          ]}
        />
        {tab === 'associates'
          && <div>
            {rows && rows.length > 0 ? <FilterTable
              rows={rows}
              headCells={headCells}
              actions={actions}
              tableId="associates-accepted"
              pagination={{ enabled: true, pageSize: 10 }}
              customButtons={customButton}
            /> : <EmptyTable
              title='No hay socios'
              info='La productora no tiene socios'
              button={[
                {
                  label: 'Agregar socio',
                  onClick: handleAddMember,
                  variant: 'secondary',
                  image: '/user-plus-black.svg',
                },
              ]}
              icon={FaUser}
              iconColor='text-primary-500' />}
          </div>}
        {tab === 'pending'
          && <div>
            {rows && rows.length > 0 ? <FilterTable
              rows={rows}
              headCells={headCells}
              actions={actions}
              tableId="associates-pending"
              pagination={{ enabled: true, pageSize: 10 }}
            /> : <EmptyTable
              title='No hay solicitudes pendientes'
              info='No hay solicitudes pendientes'
              icon={FaUser}
              iconColor='text-primary-500' />}
          </div>
        }
        {tab === 'blacklist'
          && <div>
            {rows && rows.length > 0 ? <FilterTable
              rows={rows}
              headCells={headCells}
              actions={actions}
              tableId="associates-blacklist"
              pagination={{ enabled: true, pageSize: 10 }}
              customButtons={customButton}
            /> : <EmptyTable
              title='No hay clientes en lista negra'
              info='La productora no tiene clientes en lista negra'
              button={[
                {
                  label: 'Agregar cliente',
                  onClick: handleAddMember,
                  variant: 'secondary',
                  image: '/user-plus-black.svg',
                },
              ]}
              icon={FaUser}
              iconColor='text-primary-500' />}
          </div>}
      </div>
      <CustomModal
        isOpen={showModal}
        onRequestClose={() => setShowModal(false)}
        hideCancelButton
      >
        {modalType === 'addMember'
          && <form onSubmit={handleSubmit(handleSubmitAddMember)} className='flex flex-col gap-4 w-[65vw] max-w-[600px]'>
            <h1>Agregar socio</h1>
            <div className='flex flex-col gap-4'>
              <Input id="name" label="Nombre y Apellido" labelAbove {...register('full_name')} errMsg={errors.full_name?.message} />
              <Input id="tax_number" label="DNI" labelAbove {...register('person_id')} errMsg={errors.person_id?.message} />
              <Input id="birth_date" type='date' label="Fecha de nacimiento" labelAbove {...register('birth_date')} errMsg={errors.birth_date?.message} />
              <Input id="email" label="Email" labelAbove {...register('email')} errMsg={errors.email?.message} />
              <Input id="phone" label="Número de celular" labelAbove {...register('phone_number')} errMsg={errors.phone_number?.message} />
              <Input id="referral_name" label="¿Quién te sugirió ser parte de este club?" labelAbove {...register('referral_name')} />
              <Input id="socials" label="Redes sociales" labelAbove {...register('socials')} />
              <div className='flex flex-row gap-2'>
                <Button variant='secondary' onClick={() => setShowModal(false)}>Cancelar</Button>
                <Button type='submit' loading={isCreatingMember}>Agregar</Button>
              </div>
            </div>
          </form>}
        {(modalType === 'view' || modalType === 'accept')
          && <div className='flex flex-col gap-4 w-[65vw] max-w-[600px]'>
            <h1>{modalType === 'view' ? 'Datos del socio' : 'Datos de la solicitud'}</h1>
            <div className='flex justify-center mb-4'>
              <div className='relative w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden border-2 border-border-primary'>
                {selectedRow?.photo_url ? (
                  <img
                    src={selectedRow.photo_url}
                    alt={`Foto de ${selectedRow.name}`}
                    className='w-full h-full object-cover'
                  />
                ) : (
                  <div className='w-full h-full bg-surface-secondary flex items-center justify-center'>
                    <FaUser className='w-16 h-16 text-text-tertiary' />
                  </div>
                )}
              </div>
            </div>
            <div className='flex flex-col gap-4'>
              <Input id="name" label="Nombre y Apellido" value={selectedRow?.name} labelAbove disabled />
              <Input id="tax_number" label="DNI" value={selectedRow?.tax_number} labelAbove disabled />
              <Input id="birth_date" type="date" label="Fecha de nacimiento" value={selectedRow?.birth_date} labelAbove disabled />
              <Input id="email" label="Email" value={selectedRow?.email} labelAbove disabled />
              <Input id="phone" label="Número de celular" value={selectedRow?.phone} labelAbove disabled />
              <Input id="referral_name" label="¿Quién te sugirió ser parte de este club?" value={selectedRow?.referral_name} labelAbove disabled />
              <Input id="socials" label="Redes sociales" value={selectedRow?.socials} labelAbove disabled />
              <div className='flex flex-row gap-2'>
                <Button variant='secondary' onClick={() => {
                  if (modalType === 'accept' && selectedRow) {
                    handleSubmitReject(selectedRow);
                  } else {
                    setShowModal(false);
                  }
                }} loading={isDestroyingMember}>{modalType === 'view' ? 'Cancelar' : 'Rechazar'}</Button>
                <Button type='submit' onClick={() => {
                  if (modalType === 'accept' && selectedRow) {
                    handleSubmitAccept(selectedRow);
                  } else {
                    setShowModal(false);
                  }
                }} loading={isUpdatingStatus}>{modalType === 'view' ? 'Aceptar' : 'Aprobar'}</Button>
              </div>
            </div>
          </div>}
        {modalType === 'edit'
          && <form onSubmit={handleSubmit(handleSubmitEdit)} className='flex flex-col gap-4 w-[65vw] max-w-[600px]'>
            <h1 className='w-full'>Editar datos</h1>
            <div className='flex flex-col gap-4'>
              <div className='relative mx-auto z-10'>
                <div
                  className='absolute top-1 right-4 p-1 rounded-full bg-surface-base border border-border-primary cursor-pointer'
                  onClick={() => downloadImage(selectedRow?.photo_url, `${selectedRow?.name || 'socio'}.jpg`)}
                >
                  <FiDownload className='w-6 h-6 text-text-primary' />
                </div>
                <div
                  className='absolute top-1 left-4 p-1 rounded-full bg-surface-base border border-border-primary cursor-pointer'
                  onClick={() => triggerPhotoUpload('member-photo-upload-edit')}
                >
                  <FiEdit2 className='w-6 h-6 text-text-primary' />
                </div>
              </div>
              <div className='relative w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden border-2 border-border-primary mx-auto'>
                {!selectedRow?.photo_url && (
                  <div className='w-full h-full bg-surface-secondary flex items-center justify-center'>
                    <FaUser className='w-16 h-16 text-text-tertiary' />
                  </div>
                )}
                {selectedRow?.photo_url && isUploading && (
                  <div className='w-full h-full bg-surface-secondary flex items-center justify-center'>
                    <FaSpinner className='w-16 h-16 text-text-tertiary animate-spin' />
                  </div>
                )}
                {selectedRow?.photo_url && !isUploading && (
                  <>
                    <img
                      src={photoUrl || selectedRow.photo_url}
                      alt={`Foto de ${selectedRow.name}`}
                      className='w-full h-full object-cover'
                    />
                    {selectedRow?.id && business?.id && (
                      <PhotoUpload
                        id='member-photo-upload-edit'
                        className='hidden absolute'
                        defaultPreview={''}
                        onFileUploaded={(url) => {
                          setPhotoUrl(url);
                          setIsUploading(false);
                          toastSuccess('Foto actualizada');
                        }}
                        isUploading={(isUploadingImage) => {
                          setIsUploading(isUploadingImage);
                        }}
                      />
                    )}
                  </>
                )}
              </div>
              <Input id="name" label="Nombre y Apellido" labelAbove {...register('full_name')} errMsg={errors.full_name?.message} />
              <Input id="tax_number" label="DNI" labelAbove {...register('person_id')} errMsg={errors.person_id?.message} />
              <Input id="birth_date" type='date' label="Fecha de nacimiento" labelAbove {...register('birth_date')} errMsg={errors.birth_date?.message} />
              <Input id="email" label="Email" labelAbove {...register('email')} errMsg={errors.email?.message} />
              <Input id="phone" label="Número de celular" labelAbove {...register('phone_number')} errMsg={errors.phone_number?.message} />
              <Input id="referral_name" label="¿Quién te sugirió ser parte de este club?" labelAbove {...register('referral_name')} />
              <Input id="socials" label="Redes sociales" labelAbove {...register('socials')} />
              <div className='flex flex-row gap-2'>
                <Button variant='secondary' onClick={() => setShowModal(false)}>Cancelar</Button>
                <Button type='submit' loading={isUpdatingMember}>Guardar</Button>
              </div>
            </div>
          </form>}
        {modalType === 'sendToBlacklist'
          && <div className='flex flex-col gap-4 w-[65vw] max-w-[600px]'>
            <h1>Enviar a lista negra</h1>
            <p>¿Deseas enviar a la lista negra a {selectedRow?.name}?</p>
            <p>Una vez realizada esta acción la persona será almacenada en la lista negra.</p>
            <div className='flex flex-row gap-2'>
              <Button variant='secondary' onClick={() => setShowModal(false)}>Cancelar</Button>
              <Button type='submit' onClick={() => {
                if (selectedRow) {
                  handleSubmitBlacklist(selectedRow);
                }
              }} loading={isUpdatingStatus}>Enviar</Button>
            </div>
          </div>}
        {modalType === 'remove'
          && <div className='flex flex-col gap-4 w-[65vw] max-w-[600px]'>
            <h1>Eliminar cliente</h1>
            <p>¿Deseas eliminar a {selectedRow?.name}?</p>
            <p>Una vez eliminado dejará de formar parte de tu listado de clientes y desaparecerá del sistema.</p>
            <div className='flex flex-row gap-2'>
              <Button variant='secondary' onClick={() => setShowModal(false)}>Cancelar</Button>
              <Button type='submit' onClick={() => {
                if (selectedRow) {
                  handleSubmitReject(selectedRow);
                }
              }} loading={isDestroyingMember}>Eliminar</Button>
            </div>
          </div>}
        {modalType === 'resend'
          && <div className='flex flex-col gap-4 w-[65vw] max-w-[600px]'>
            <h1>Reenviar solicitud de socio</h1>
            <p>¿Deseas reenviar la solicitud de socio a {selectedRow?.name}?</p>
            <p>Una vez realizada esta acción la persona recibirá un correo electrónico con la solicitud de socio.</p>
            <div className='flex flex-row gap-2'>
              <Button variant='secondary' onClick={() => setShowModal(false)}>Cancelar</Button>
              <Button type='submit'>Reenviar</Button>
            </div>
          </div>}
        {modalType === 'reset'
          && <div className='flex flex-col gap-4 w-[65vw] max-w-[600px]'>
            <h1>Restablecer cliente</h1>
            <p>¿Deseas restablecer a {selectedRow?.name}?</p>
            <p>Una vez realizada esta acción el cliente volverá a formar parte de tu listado de socios.</p>
            <div className='flex flex-row gap-2'>
              <Button variant='secondary' onClick={() => setShowModal(false)}>Cancelar</Button>
              <Button type='submit' onClick={() => handleSubmitReset(selectedRow as TableRow)} loading={isUpdatingStatus}>Restablecer</Button>
            </div>
          </div>}
      </CustomModal>
    </div>
  );
};

export default MembersPage;
